import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { Lock, User, Eye, EyeOff, KeyRound, AlertTriangle, ArrowLeft, Mail, CheckCircle2, Loader2 } from "lucide-react";
import api from "../connector/api";

const Login = ({ setToken }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // View States
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [isResetSent, setIsResetSent] = useState(false);

  // Lockout States
  const [isLocked, setIsLocked] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  // Password Force Change States
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);

  const navigate = useNavigate();

  // Initial Load Check for active lockout
  useEffect(() => {
    const lockoutUntil = localStorage.getItem("lockout_until");
    if (lockoutUntil) {
      const currentTime = Date.now();
      const remainingTime = Math.floor((parseInt(lockoutUntil, 10) - currentTime) / 1000);

      if (remainingTime > 0) {
        setIsLocked(true);
        setSecondsLeft(remainingTime);
      } else {
        localStorage.removeItem("lockout_until");
      }
    }
  }, []);

  // Active Countdown Timer logic
  useEffect(() => {
    let interval = null;
    if (isLocked && secondsLeft > 0) {
      interval = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            setIsLocked(false);
            localStorage.removeItem("lockout_until");
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isLocked, secondsLeft]);

  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  // Standard Login Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLocked) {
      toast.error(`Interface locked. Please wait ${formatTime(secondsLeft)}`);
      return;
    }

    const cleanUsername = username.trim();
    const cleanPassword = password.trim();

    if (!cleanUsername || !cleanPassword) {
      toast.error("Username and password fields cannot be empty.");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("token/", { username: cleanUsername, password: cleanPassword });
      const storage = rememberMe ? localStorage : sessionStorage;

      storage.setItem("access_token", res.data.access);
      storage.setItem("refresh_token", res.data.refresh);

      if (res.data.password_change_required) {
        toast.custom((t) => (
          <div className="bg-amber-50 border border-amber-200 p-3 rounded-xl text-xs font-medium text-amber-800 shadow-sm flex items-center gap-2">
            <KeyRound size={16} className="text-amber-600 flex-shrink-0" />
            Temporary key active. System settings require a mandatory security update.
          </div>
        ));
        setMustChangePassword(true);
        setLoading(false);
        return;
      }

      const me = await api.get("accounts/me/", {
        headers: { Authorization: `Bearer ${res.data.access}` },
      });

      storage.setItem("user", JSON.stringify(me.data.user));
      storage.setItem("permissions", JSON.stringify(me.data.permissions));
      
      if (typeof setToken === "function") {
        setToken(res.data.access);
      }

      localStorage.removeItem("lockout_until");

      const displayName = (me.data.user.first_name || cleanUsername).replace(/[<>]/g, "");
      toast.success(`Welcome back, ${displayName}!`);

      // Group-based Routing Logic
      const userGroups = me.data.user.groups || [];
      const primaryGroup = me.data.user.group_name;

      if (primaryGroup === "Faculty" || userGroups.includes("Faculty") || me.data.user.role === "Faculty") {
        navigate("/teacher/dashboard", { replace: true });
      } else if (me.data.user.role === "super_admin") {
        navigate("/super-admin/dashboard", { replace: true });
      } else if (me.data.user.role === "branch_admin") {
        navigate("/branch-admin/dashboard", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }

    } catch (err) {
      const status = err.response?.status;
      if (status === 403 || status === 429) {
        setIsLocked(true);
        const lockoutDurationSeconds = 1800;
        setSecondsLeft(lockoutDurationSeconds);

        const lockoutUntilTimestamp = Date.now() + lockoutDurationSeconds * 1000;
        localStorage.setItem("lockout_until", lockoutUntilTimestamp.toString());

        if (status === 429) {
          toast.error("Too many login requests. Rate limit exceeded.");
        } else {
          toast.error("Access suspended. Security threshold breached.");
        }
      } else {
        toast.error(err.response?.data?.detail || "Invalid credentials. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Password Modification Submission Handler
  const handlePasswordUpdateSubmit = async (e) => {
    e.preventDefault();

    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Password inputs do not match.");
      return;
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem("access_token") || localStorage.getItem("access_token");

      await api.post(
        "accounts/change-password/",
        {
          old_password: password,
          new_password: newPassword,
          confirm_password: confirmPassword,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Credentials updated successfully!");

      const me = await api.get("accounts/me/", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem("user", JSON.stringify(me.data.user));
      storage.setItem("permissions", JSON.stringify(me.data.permissions));

      if (typeof setToken === "function") {
        setToken(token);
      }

      setMustChangePassword(false);
      navigate("/dashboard");
    } catch (err) {
      const backendMessage = err.response?.data?.old_password || err.response?.data?.detail;
      toast.error(backendMessage || "Failed to update password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Request Reset Email Handler
  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Email address required");
      return;
    }

    setLoading(true);
    try {
      await api.post("my-password/request-reset/", { email });
      setIsResetSent(true);
      toast.success("Recovery instructions sent");
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to initiate reset");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center font-sans bg-zinc-50/80 text-zinc-600 text-xs font-medium select-none p-4">
      <Toaster position="top-right" />

      {/* Main Authentication Card */}
      <div className="bg-white w-full max-w-sm rounded-lg border border-zinc-200/80 shadow-sm p-4 md:p-6 transition-all">
        
        {/* Header Branding */}
        <div className="text-center mb-6">
          <div className="w-20 h-20 mx-auto mb-3 flex items-center justify-center">
            <img 
              src="/logo.png" 
              alt="CAIS Logo" 
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-base font-bold text-zinc-900 tracking-tight">
           Cambodia Adventist International School
          </h1>
          <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mt-1">
            {mustChangePassword
              ? "Account Security Portal"
              : isForgotMode
              ? "Account Recovery Portal"
              : "Management Portal Login"}
          </p>
        </div>

        {/* Real-time Lockout Alert */}
        {isLocked && (
          <div className="mb-4 p-3.5 rounded-xl border border-red-200 bg-red-50/50 text-center animate-fadeIn">
            <div className="flex items-center justify-center gap-1.5 text-red-600 mb-1">
              <AlertTriangle size={15} />
              <p className="text-[11px] font-bold uppercase tracking-wider">Account Suspended</p>
            </div>
            <p className="text-[11px] text-zinc-600 leading-normal">
              Too many failed login attempts. Contact your administrator for immediate assistance.
            </p>
            <span className="inline-block mt-2 text-[11px] font-mono font-bold tracking-wider text-red-700 bg-red-100 px-2.5 py-0.5 rounded">
              Time Remaining: {formatTime(secondsLeft)}
            </span>
          </div>
        )}

        {/* Dynamic Rendering Forms */}
        {mustChangePassword ? (
          /* --- MANDATORY PASSWORD RESET FORM --- */
          <form onSubmit={handlePasswordUpdateSubmit} className="space-y-3.5">
            <div>
              <label className="block text-[11px] font-semibold text-zinc-700 mb-1">
                New Secure Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={15} />
                <input
                  type={showNewPassword ? "text" : "password"}
                  className="w-full pl-9 pr-9 py-2 text-xs bg-zinc-50 border border-zinc-200/80 rounded-lg text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:bg-white focus:border-zinc-400 transition-all font-medium"
                  placeholder="Minimum 8 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
                >
                  {showNewPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-zinc-700 mb-1">
                Verify New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={15} />
                <input
                  type="password"
                  className="w-full pl-9 pr-3 py-2 text-xs bg-zinc-50 border border-zinc-200/80 rounded-lg text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:bg-white focus:border-zinc-400 transition-all font-medium"
                  placeholder="Confirm matching values"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-[#002855] hover:bg-[#001d3d] text-white rounded-lg text-xs font-semibold shadow-xs transition-colors disabled:opacity-50 flex items-center justify-center gap-2 mt-2"
            >
              {loading ? <Loader2 size={15} className="animate-spin" /> : "Commit Password Update"}
            </button>
          </form>
        ) : !isForgotMode ? (
          /* --- STANDARD LOGIN FORM --- */
          <form onSubmit={handleSubmit} className="space-y-3.5">
            <div>
              <label className="block text-[11px] font-semibold text-zinc-700 mb-1">
                Username
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={15} />
                <input
                  type="text"
                  disabled={isLocked}
                  className={`w-full pl-9 pr-3 py-2 text-xs rounded-lg transition-all font-medium focus:outline-none ${
                    isLocked
                      ? "bg-zinc-100 border-zinc-200 text-zinc-400 cursor-not-allowed"
                      : "bg-zinc-50 border border-zinc-200/80 text-zinc-900 placeholder:text-zinc-400 focus:bg-white focus:border-zinc-400"
                  }`}
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-zinc-700 mb-1">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={15} />
                <input
                  type={showPassword ? "text" : "password"}
                  disabled={isLocked}
                  className={`w-full pl-9 pr-9 py-2 text-xs rounded-lg transition-all font-medium focus:outline-none ${
                    isLocked
                      ? "bg-zinc-100 border-zinc-200 text-zinc-400 cursor-not-allowed"
                      : "bg-zinc-50 border border-zinc-200/80 text-zinc-900 placeholder:text-zinc-400 focus:bg-white focus:border-zinc-400"
                  }`}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  disabled={isLocked}
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors disabled:opacity-50"
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between pt-0.5">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input 
                  type="checkbox" 
                  disabled={isLocked}
                  className="w-3.5 h-3.5 rounded border-zinc-300 text-[#002855] focus:ring-[#002855]/20 accent-[#002855]" 
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span className="text-[11px] font-medium text-zinc-500 group-hover:text-zinc-800 transition-colors">
                  Remember me
                </span>
              </label>
              <button 
                type="button"
                onClick={() => setIsForgotMode(true)}
                className="text-[11px] text-zinc-500 hover:text-zinc-900 transition-colors font-semibold"
              >
                Forgot Password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading || isLocked}
              className={`w-full py-2.5 rounded-lg text-xs font-semibold shadow-xs transition-colors flex items-center justify-center gap-2 mt-2 ${
                isLocked
                  ? "bg-red-50 border border-red-200 text-red-600 cursor-not-allowed font-mono"
                  : "bg-[#002855] hover:bg-[#001d3d] text-white disabled:opacity-50"
              }`}
            >
              {loading ? <Loader2 size={15} className="animate-spin" /> : "Sign In"}
            </button>
          </form>
        ) : (
          /* --- FORGOT PASSWORD VIEW --- */
          <div className="space-y-4">
            {isResetSent ? (
              <div className="text-center py-3 space-y-2">
                <div className="flex justify-center text-emerald-600 mb-1">
                  <CheckCircle2 size={32} />
                </div>
                <p className="text-zinc-700 text-xs leading-relaxed">
                  We've sent reset instructions to <br/>
                  <span className="font-semibold text-zinc-900">{email}</span>
                </p>
                <p className="text-[10px] text-zinc-400 uppercase font-bold tracking-wider pt-1">
                  Check your inbox or spam
                </p>
              </div>
            ) : (
              <>
                <p className="text-zinc-500 text-xs text-center leading-relaxed">
                  Enter your registered email address to receive password reset instructions.
                </p>
                <div>
                  <label className="block text-[11px] font-semibold text-zinc-700 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={15} />
                    <input
                      type="email"
                      className="w-full pl-9 pr-3 py-2 text-xs bg-zinc-50 border border-zinc-200/80 rounded-lg text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:bg-white focus:border-zinc-400 transition-all font-medium"
                      placeholder="name@school.edu"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <button
                  onClick={handleRequestReset}
                  disabled={loading}
                  className="w-full py-2.5 bg-[#002855] hover:bg-[#001d3d] text-white rounded-lg text-xs font-semibold shadow-xs transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 size={15} className="animate-spin" /> : "Send Reset Link"}
                </button>
              </>
            )}
            
            <button
              onClick={() => { setIsForgotMode(false); setIsResetSent(false); }}
              className="w-full flex items-center justify-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 transition-colors pt-1 font-medium"
            >
              <ArrowLeft size={13} /> Back to Sign In
            </button>
          </div>
        )}

        {/* Card Footer Divider */}
        <div className="mt-6 pt-4 border-t border-zinc-100 text-center">
          <span className="text-[10px] text-zinc-400 font-medium">
            Authorized Personnel Only
          </span>
        </div>
      </div>
    </div>
  );
};

export default Login;